import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { NO_SWIPE_ATTRIBUTE } from '../../hooks/useSwipeTabs';
import { cn } from '../../lib/utils';

type EmblaCarouselType = NonNullable<UseEmblaCarouselType[1]>;
type EmblaOptionsType = NonNullable<Parameters<typeof useEmblaCarousel>[0]>;

interface SwipePagerProps {
  /** The pages, in the order of the tabs that name them. */
  ids: string[];
  /** The page on screen — the open tab. */
  activeId: string;
  /** A swipe settled on another page. */
  onChange: (id: string) => void;
  renderPage: (id: string) => ReactNode;
  className?: string;
  /** On each page — e.g. the gutters, when the pager itself is full-bleed. */
  pageClassName?: string;
  /**
   * Once, shortly after it opens: the page leans towards the next one and comes
   * back, so the reader learns there is something beside it to swipe to.
   */
  peek?: boolean;
  /**
   * The hint has just played — never when it is skipped (one page, reduced
   * motion) — for a page that shows it only the first time.
   */
  onPeek?: () => void;
}

/** How far the hint leans, as a share of the page's width, and for how long. */
const PEEK_SHARE = 0.18;
const PEEK_MS = 900;
const PEEK_DELAY_MS = 600;

/**
 * A tab's content that follows the finger: dragged sideways, the page slides
 * and the next one comes in beside it, and letting go settles on whichever is
 * more in view. The tabs above stay the way to name a page; this is only the
 * gesture, with no arrows and no dots.
 *
 * **Only the open page is filled.** The others are empty slots until something
 * moves — a drag, or the next tab picked above — and then just the pages either
 * side are drawn, and emptied again once it settles. A stage runs
 * a handful of classes, each with a list of its own, and every one of them
 * mounted at once would be a page of tables nobody is looking at (and four rows
 * called "Blocco 1" to anything reading the page as text).
 *
 * A page's height is its own: the empty slots have none, so the pager is as
 * tall as the open page. While a neighbour is drawn it may stretch it for the
 * length of the swipe. Embla re-measures only on a change of *width*, which
 * filling a page never makes, so drawing one mid-drag does not cut the drag.
 *
 * Vertical scrolling stays the browser's (`touch-action: pan-y`), and a drag
 * that starts inside something marked with `NO_SWIPE_ATTRIBUTE` — a table that
 * scrolls sideways itself — is left to it.
 */
export function SwipePager({
  ids,
  activeId,
  onChange,
  renderPage,
  className,
  pageClassName,
  peek = false,
  onPeek,
}: SwipePagerProps) {
  const activeIndex = Math.max(0, ids.indexOf(activeId));

  // Stable for the pager's life: embla re-initialises whenever its options
  // compare different, and a re-init mid-swipe drops the swipe.
  const startIndex = useRef(activeIndex).current;
  const options = useMemo<EmblaOptionsType>(
    () => ({
      startIndex,
      watchDrag: (_api, event) =>
        !(event.target instanceof Element && event.target.closest(`[${NO_SWIPE_ATTRIBUTE}]`)),
    }),
    [startIndex]
  );
  const [viewportRef, embla] = useEmblaCarousel(options);

  /** The pages drawn besides the open one, while something is moving. */
  const [span, setSpan] = useState<[number, number] | null>(null);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;

  useEffect(() => {
    if (!embla) return;

    // A drag under way: its neighbours, on both sides — which way it is going
    // is only known when it is let go.
    const onScroll = (api: EmblaCarouselType) => {
      const at = api.selectedScrollSnap();
      setSpan((current) => current ?? [at - 1, at + 1]);
    };
    const onSettle = () => setSpan(null);
    const onSelect = (api: EmblaCarouselType) => {
      const id = idsRef.current[api.selectedScrollSnap()];
      if (id && id !== activeIdRef.current) onChangeRef.current(id);
    };

    embla.on('scroll', onScroll).on('settle', onSettle).on('select', onSelect);
    return () => {
      embla.off('scroll', onScroll).off('settle', onSettle).off('select', onSelect);
    };
  }, [embla]);

  // A tab picked above. The next one over slides in, with the page it leaves
  // still drawn (set before paint, or it would blank for a frame); one further
  // away is jumped to — sliding past pages that are not drawn shows nothing.
  //
  // Only when there is somewhere to go: embla says it has settled only after
  // an animation, so a span set for a move that never happens would stay.
  useLayoutEffect(() => {
    if (!embla) return;
    const from = embla.selectedScrollSnap();
    if (from === activeIndex || activeIndex >= embla.scrollSnapList().length) return;

    const far = Math.abs(from - activeIndex) > 1;
    if (!far) setSpan([Math.min(from, activeIndex), Math.max(from, activeIndex)]);
    embla.scrollTo(activeIndex, far);
  }, [embla, activeIndex]);

  // The hint: the neighbour drawn, then the strip of pages nudged towards it
  // and back. Added on top of embla's own transform (composite 'add') rather
  // than through it, so embla never learns of it and a finger landing midway
  // finds the pager where it left it.
  const containerRef = useRef<HTMLDivElement>(null);
  const peeked = useRef(false);
  const onPeekRef = useRef(onPeek);
  onPeekRef.current = onPeek;
  useEffect(() => {
    const container = containerRef.current;
    if (!peek || peeked.current || !embla || !container || ids.length < 2) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof container.animate !== 'function') return;

    let animation: Animation | null = null;
    const timer = window.setTimeout(() => {
      peeked.current = true;
      // Towards the next page, or the previous one from the last.
      const toward = activeIndex < ids.length - 1 ? 1 : -1;
      setSpan([Math.min(activeIndex, activeIndex + toward), Math.max(activeIndex, activeIndex + toward)]);
      const shift = `translateX(${-toward * PEEK_SHARE * 100}%)`;
      animation = container.animate(
        [
          { transform: 'translateX(0)', easing: 'cubic-bezier(0.3, 0, 0.2, 1)' },
          { transform: shift, offset: 0.4, easing: 'cubic-bezier(0.5, 0, 0.3, 1.4)' },
          { transform: 'translateX(0)' },
        ],
        { duration: PEEK_MS, composite: 'add' }
      );
      animation.onfinish = () => setSpan(null);
      onPeekRef.current?.();
    }, PEEK_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      animation?.cancel();
    };
    // Once per pager: the athlete changing afterwards is the reader swiping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peek, embla]);

  const filled = (index: number) =>
    index === activeIndex || (span !== null && index >= span[0] && index <= span[1]);

  return (
    <div ref={viewportRef} className={cn('overflow-hidden', className)} data-testid="swipe-pager">
      <div ref={containerRef} className="flex items-start [touch-action:pan-y_pinch-zoom]">
        {ids.map((id, index) => (
          <div
            key={id}
            className={cn('min-w-0 shrink-0 grow-0 basis-full', pageClassName)}
            aria-hidden={index !== activeIndex}
            data-testid="swipe-pager-page"
          >
            {filled(index) ? renderPage(id) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
